/** DynamicObject (respawnable dynamic props / enemies), Squishable and DespawnTimer. */
import { GameBehaviour } from '../game.ts';
import { BodyType, type ContactPoint, type SimCollider, type SimGo } from '../world.ts';
import { FrameCounter, PrimaryWaysToDie, vangle, vdot, type Deathod, type RGBA, type V2 } from '../unity.ts';
import { compose, invert, mul } from '../../../model/xform.ts';
import { bool, list, num } from '../props.ts';
import { killRespawnables } from '../player.ts';
import { ActivatorBase } from './activators.ts';
import { blocksRespawn, playerOf } from './util.ts';
import { AnimatedEffect } from './animator.ts';

/** Squishable (added by DynamicObject.Awake; the player has its own copy inline) */
export class Squishable extends GameBehaviour {
  penetrationThreshold = 0.1;
  squishCounter = new FrameCounter(4);

  override awake() {
    this.penetrationThreshold = num(this.data.penetrationThreshold, 0.1);
  }

  static addToSelf(go: SimGo): Squishable {
    const own = go.getBehaviour(Squishable);
    if (own) return own;
    const s = new Squishable(go, {});
    s.unityType = 'Squishable';
    go.world.addBehaviour(s);
    return s;
  }

  override fixedUpdate() {
    const rb = this.go.body;
    if (!rb) return;
    const all = rb.getContacts().slice(0, 16);
    if (all.length < 2) return;
    const blocking = all.filter(isBlockingContact);
    let overlap = false;
    if (blocking.length >= 2) {
      let best = 1;
      let a: ContactPoint | null = null, b: ContactPoint | null = null;
      for (let i = 0; i < blocking.length; i++) {
        for (let j = i + 1; j < blocking.length; j++) {
          const d = vdot(blocking[i].normal, blocking[j].normal);
          if (d < best) {
            best = d;
            a = blocking[i];
            b = blocking[j];
          }
        }
      }
      if (best < 0 && a && b) {
        if ((-a.separation > this.penetrationThreshold && !effectorOf(a)) || (-b.separation > this.penetrationThreshold && !effectorOf(b))) overlap = true;
      }
    }
    if (overlap) {
      if (this.squishCounter.isNotRunning()) this.squishCounter.start();
      else if (this.squishCounter.countCheckFinishReset()) {
        this.squishCounter.reset();
        killRespawnables(this.go, { primary: PrimaryWaysToDie.Squish });
      }
    } else this.squishCounter.reset();
  }
}

function effectorOf(cp: ContactPoint): SimCollider | null {
  if (cp.collider.usedByEffector && cp.collider.effector) return cp.collider;
  if (cp.otherCollider.usedByEffector && cp.otherCollider.effector) return cp.otherCollider;
  return null;
}

function isBlockingContact(cp: ContactPoint): boolean {
  if (cp.collider.isTrigger || cp.otherCollider.isTrigger) return false;
  const pe = effectorOf(cp);
  if (!pe || !pe.effector!.useOneWay) return true;
  return vangle(pe.go.up, cp.normal) <= pe.effector!.arc * 0.5;
}

/** Renderer.enabled for every renderer in a subtree (inactive children included) */
export function toggleRenderers(go: SimGo, on: boolean) {
  for (const g of go.descendants()) {
    if (g.sprite) g.sprite.enabled = on;
    if (g.tilemap) g.tilemap.enabled = on;
  }
}

const WAYS_TO_DIE = [PrimaryWaysToDie.DeathZone, PrimaryWaysToDie.KillActivator, PrimaryWaysToDie.Squish];

export class DynamicObject extends GameBehaviour {
  canDie = true;
  canRespawn = true;
  startDespawned = false;
  activatorForcesRespawn = false;
  killActivatorTriggeredThisFrame = false;
  respawnActivatorTriggeredThisFrame = false;
  startPosition: V2 = { x: 0, y: 0 };
  startRotation = 0;
  tryRespawn = false;
  forceRespawn = false;
  deathCounter = FrameCounter.seconds(1.5);
  respawnCounter = FrameCounter.seconds(1);
  isSpawned = true;
  lastDynamicVelocity: V2 = { x: 0, y: 0 };
  fadeFromColor: RGBA = [1, 1, 1, 1];
  fadeToColor: RGBA = [1, 1, 1, 1];
  fadeCounter = FrameCounter.seconds(0.5);
  collider: SimCollider | null = null;
  dontRotate: SimGo[] = [];
  matchPlayerColorSprites: SimGo[] = [];
  override noSave = ['collider', 'dontRotate', 'matchPlayerColorSprites'];

  override awake() {
    const d = this.data;
    this.canDie = bool(d.canDie, true);
    this.canRespawn = bool(d.canRespawn, true);
    this.startDespawned = bool(d.startDespawned);
    this.activatorForcesRespawn = bool(d.activatorForcesRespawn);
    Squishable.addToSelf(this.go);
    this.matchPlayerColorSprites = list(d.matchPlayerColorSprites).map((r) => this.refGo(r)).filter((g): g is SimGo => !!g?.sprite);
    if (this.matchPlayerColorSprites.length > 1) {
      const c = this.matchPlayerColorSprites[0].sprite!.color;
      for (const g of this.matchPlayerColorSprites) g.sprite!.color = [...c] as RGBA;
    }
  }

  override start() {
    this.dontRotate = list(this.data.dontRotateTransforms).map((r) => this.refGo(r)).filter((g): g is SimGo => !!g);
    const rb = this.go.body;
    if (rb) {
      this.startPosition = rb.position;
      this.startRotation = rb.rotation;
    }
    const all: SimCollider[] = [];
    const walk = (g: SimGo) => {
      if (!g.active) return;
      for (const c of g.colliders) if (c.enabled) all.push(c);
      g.children.forEach(walk);
    };
    walk(this.go);
    this.collider = all.find((c) => !c.isTrigger) ?? null;
    const kill = this.refBehaviour(this.data.killActivator, ActivatorBase as any) as ActivatorBase | null;
    kill?.listeners.push((on) => {
      if (on) this.killActivatorTriggeredThisFrame = true;
    });
    const respawn = this.refBehaviour(this.data.respawnActivator, ActivatorBase as any) as ActivatorBase | null;
    respawn?.listeners.push((on) => {
      if (on) this.respawnActivatorTriggeredThisFrame = true;
    });
    if (this.startDespawned) this.finishDeath(false);
  }

  override update() {
    for (const t of this.dontRotate) {
      if (Math.abs(t.rotation) < 1e-6) continue;
      const [sx, sy] = t.lossyScale;
      const p = t.position;
      const pw = t.parent ? t.parent.worldXform() : null;
      const w = compose(p.x, p.y, 0, sx, sy);
      t.setLocal(pw ? mul(invert(pw), w) : w);
    }
  }

  override fixedUpdate() {
    const rb = this.go.body;
    if (!rb) return;
    this.enumerateContacts();
    if (this.fadeCounter.isRunningNotFinished()) {
      this.fadeCounter.count();
      const t = this.fadeCounter.range(0, 1);
      const a = this.fadeFromColor, b = this.fadeToColor;
      const c: RGBA = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t, a[3] + (b[3] - a[3]) * t];
      for (const g of this.matchPlayerColorSprites) g.sprite!.color = [...c] as RGBA;
    }
    if (this.killActivatorTriggeredThisFrame) {
      this.killActivatorTriggeredThisFrame = false;
      if (this.isSpawned) this.finishDeath();
    }
    if (this.respawnActivatorTriggeredThisFrame) {
      this.respawnActivatorTriggeredThisFrame = false;
      if (!this.isSpawned || this.activatorForcesRespawn) {
        this.finishDeath(false);
        this.tryRespawn = true;
        this.forceRespawn = true;
        this.respawnCounter.reset();
        this.deathCounter.reset();
      }
    }
    if (this.deathCounter.countCheckFinishReset()) this.finishDeath();
    if (this.respawnCounter.countCheckFinishReset()) this.tryRespawn = true;
    if (!this.isSpawned) this.tryToRespawn();
    this.lastDynamicVelocity = rb.velocity;
  }

  setMatchPlayerColor(color: RGBA, fade = true) {
    if (!this.matchPlayerColorSprites.length) return;
    if (fade) {
      this.fadeFromColor = [...this.matchPlayerColorSprites[0].sprite!.color] as RGBA;
      this.fadeToColor = [...color] as RGBA;
      this.fadeCounter.restart();
      return;
    }
    for (const g of this.matchPlayerColorSprites) g.sprite!.color = [...color] as RGBA;
  }

  private enumerateContacts() {
    for (const cp of this.go.body!.getContacts()) {
      const p = cp.rigidbody ? playerOf(cp.rigidbody.go) : null;
      if (p) this.setMatchPlayerColor(p.go.sprite?.color ?? p.color);
    }
  }

  private tryToRespawn(): boolean {
    const should = (this.canRespawn || this.forceRespawn) && this.tryRespawn;
    if (should && this.collider) {
      if (!this.world.overlapCollider(this.collider).some((c) => blocksRespawn(c, true))) this.respawn();
    }
    return should;
  }

  beginDeath(d: Deathod) {
    if (!this.canDie) return;
    if (!WAYS_TO_DIE.includes(d.primary) || !this.isSpawned) return;
    if (d.primary === PrimaryWaysToDie.Squish) this.finishDeath();
    else {
      const dt = this.go.getBehaviour(DespawnTimer);
      if (dt) dt.startFadeOut();
      else this.deathCounter.start();
    }
  }

  finishDeath(startRespawnCounter = true, skipAnimation = false) {
    const rb = this.go.body;
    const diedAt = this.go.position;
    toggleRenderers(this.go, false);
    if (rb) {
      rb.position = this.startPosition;
      rb.rotation = this.startRotation;
      rb.angularVelocity = 0;
      rb.velocity = { x: 0, y: 0 };
      rb.type = BodyType.Static;
    }
    this.collider?.setTrigger(true);
    this.isSpawned = false;
    const despawnAnimation = this.data.despawnAnimation?.guid;
    if (despawnAnimation && !skipAnimation) AnimatedEffect.spawn(this.game, diedAt, String(despawnAnimation));
    if (startRespawnCounter) this.respawnCounter.restart();
  }

  respawn() {
    const rb = this.go.body;
    if (rb) rb.type = BodyType.Dynamic;
    this.collider?.setTrigger(false);
    this.isSpawned = true;
    this.tryRespawn = false;
    this.forceRespawn = false;
    toggleRenderers(this.go, true);
  }
}

export class DespawnTimer extends GameBehaviour {
  despawnTimer = new FrameCounter();
  destroyOnDespawn = false;
  fadeOutTimer = FrameCounter.seconds(1);
  disableColliderOnFade = true;
  staticRigidBodyOnFade = false;
  initialBodyType = BodyType.Dynamic;
  sprites: SimGo[] = [];
  override noSave = ['sprites'];

  override awake() {
    const d = this.data;
    this.despawnTimer = FrameCounter.from(d.despawnTimer, 0);
    this.destroyOnDespawn = bool(d.destroyOnDespawn);
    this.fadeOutTimer = FrameCounter.from(d.fadeOutTimer, 1);
    this.disableColliderOnFade = bool(d.disableColliderOnFade, true);
    this.staticRigidBodyOnFade = bool(d.staticRigidBodyOnFade);
    this.sprites = this.go.descendants().filter((g) => g.sprite && g.active);
  }

  override start() {
    if (this.despawnTimer.frames > 0) this.despawnTimer.start();
  }

  override fixedUpdate() {
    if (this.despawnTimer.countCheckFinishReset()) this.startFadeOut();
    if (this.fadeOutTimer.isRunningNotFinished()) this.updateAlpha();
    if (this.fadeOutTimer.countCheckFinishReset()) {
      if (this.destroyOnDespawn) this.go.destroy();
      else {
        const dyn = this.go.getBehaviour(DynamicObject);
        if (dyn) {
          dyn.finishDeath(true);
          if (this.disableColliderOnFade && this.go.colliders[0]) this.go.colliders[0].enabled = true;
          if (this.staticRigidBodyOnFade && this.go.body) this.go.body.type = this.initialBodyType;
          this.updateAlpha();
        }
      }
    }
  }

  startFadeOut() {
    if (this.disableColliderOnFade && this.go.colliders[0]) this.go.colliders[0].enabled = false;
    if (this.staticRigidBodyOnFade && this.go.body) {
      this.initialBodyType = this.go.body.type;
      this.go.body.type = BodyType.Static;
    }
    this.fadeOutTimer.start();
  }

  updateAlpha() {
    const a = this.fadeOutTimer.range(1, 0);
    for (const g of this.sprites) {
      const c = g.sprite!.color;
      g.sprite!.color = [c[0], c[1], c[2], a];
    }
  }
}
